import {Store} from 'redux';
import {from, Subject} from 'rxjs';
import {concatMap, distinctUntilChanged, map} from 'rxjs/operators';

import {readApiModel} from '../../../libs/shared/core/src/helpers/api/apiHelpers';
import {keyValueStore} from '../../../libs/shared/core/src/services/storage/storage';
import {ApiLocalPreferences, LocalPreferencesModel} from '../../../src/models/api/localPreferencesModel';
import {AppState} from '../../../src/store/appState';
import {getLocalPreferences} from '../../../src/store/data/localPreferences/localPreferencesSelectors';

/*
 * Constants.
 */

export const localPreferencesKey = 'localPreferences';

/*
 * Local preferences.
 */

/** Save the localPreferences part of the state when it changes. */
export function setupLocalPreferences(store: Store<AppState>) {
  const stateUpdates = new Subject<AppState>();

  stateUpdates
    .pipe(
      map((state) => getLocalPreferences(state)),
      distinctUntilChanged(),
      concatMap((localPreferences) => from(setLocalPreferences(localPreferences))),
    )
    .subscribe();

  store.subscribe(() => stateUpdates.next(store.getState()));
}

export function readLocalPreferences(userId: number) {
  const serializedPreferences = keyValueStore.read(localPreferencesKey);
  const localPreferences = serializedPreferences ? JSON.parse(serializedPreferences) : {};

  // If we have an userId, make sure the preferences user id match with the logged in user id.
  if (localPreferences.userId && localPreferences.userId !== userId) {
    return readApiModel(ApiLocalPreferences, {
      userId,
    });
  }
  // Attach the user id to the local preferences. This will help the transition period where we do
  // not have the user id in the local preferences.
  return readApiModel(ApiLocalPreferences, {
    userId,
    ...localPreferences,
  });
}

async function setLocalPreferences(localPreferences: LocalPreferencesModel) {
  const serializedPreferences = JSON.stringify(ApiLocalPreferences.serialize(localPreferences));
  return keyValueStore.set(localPreferencesKey, serializedPreferences);
}
